/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useState, useEffect, useRef, FC, ChangeEvent, MouseEvent, KeyboardEvent } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import ReactTooltip from 'react-tooltip';

import * as Styled from './styles';
import * as contentLibraryAction from '../../../../../actions/contentLibrary';
import eventService from '../../../../../common/eventService';
import DeleteModal from './DeleteModal';
import { request } from '../../../../../common/request';
import configs from '../../../../../constants/configs';
import { ERR_FOLDER_NAME_EDIT, ERR_FOLDER_NAME_TOO_LONG, ERR_NEW_FOLDER } from '../../../../../constants/Errors';
import { TAppState } from '../../../../../reducers';
import { IFolderColumnProps } from '../../../../../types/imageLibrary';
import handleKeyPress from '../../../../../common/handleKeyPress';

const MAX_FOLDER_NAME_CHAR = 45;
const MAX_FOLDER_CHAR_DISPLAY = 17;
const CHARS_TO_DISPLAY_WITH_ICONS = 12;

const FolderColumn: FC<IFolderColumnProps> = ({ folders, dispatch, deselectAll, selectedFolder, canManageLibrary }) => {
    const [isAddingNewFolder, setIsAddingNewFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [folderHovered, setFolderHovered] = useState<number | null>();
    const [editingFolder, setEditingFolder] = useState<number | null>();
    const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);
    const [deleteFolderID, setDeleteFolderID] = useState<number | null>();
    const addNewFolderDiv = useRef<HTMLDivElement>(null);
    const newFolderNameInput = useRef<HTMLInputElement>(null);
    const editFolderNameInput = useRef<HTMLInputElement>(null);

    // if user clicks on the add folder button more for the first time, this will trigger
    useEffect(() => {
        if (newFolderNameInput && newFolderNameInput.current) {
            newFolderNameInput.current.focus();
        }

        if (isAddingNewFolder && addNewFolderDiv && addNewFolderDiv.current) {
            addNewFolderDiv.current.scrollIntoView();
        }
    }, [isAddingNewFolder, newFolderNameInput]);

    useEffect(() => {
        if (editFolderNameInput && editFolderNameInput.current) {
            editFolderNameInput.current.focus();
        }
    }, [editingFolder, editFolderNameInput]);

    const returnFolderName = (folderName: string, folderID: number) => {
        // When the folder name is too long to fit in the panel
        if (folderName.length > MAX_FOLDER_CHAR_DISPLAY && folderHovered !== folderID) {
            return `${folderName.substring(0, MAX_FOLDER_CHAR_DISPLAY)}...`;
        }
        // when the folder name is too long when the icons appear on hover
        if (folderHovered === folderID && folderName.length > CHARS_TO_DISPLAY_WITH_ICONS) {
            return `${folderName.substring(0, CHARS_TO_DISPLAY_WITH_ICONS)}...`;
        }
        return folderName;
    };

    const addNewFolder = async () => {
        try {
            setIsAddingNewFolder(false);
            const { folderID } = await request.post(configs.contentLibraryFolder, {
                folderName: newFolderName,
            });
            dispatch(contentLibraryAction.updateFolders({ folderID, folderName: newFolderName }));
        } catch (err) {
            console.error(err);
            eventService.notify(err.customMessage ? err.customMessage : ERR_NEW_FOLDER, true);
        }
    };

    const updateFolderName = async (folderName: string, folderID: number) => {
        try {
            await request.put(configs.updateFolder, { folderName, folderID });
            dispatch(contentLibraryAction.updateFolderName({ folderID, folderName }));
        } catch (err) {
            console.error(err);
            eventService.notify(err.customMessage ? err.customMessage : ERR_FOLDER_NAME_EDIT, true);
        }
    };

    const toggleDeleteModal = (folderID: number | null) => {
        setDeleteFolderID(folderID);
        setShowDeleteWarningModal(!showDeleteWarningModal);
    };

    const onSelectFolderClick = (e: MouseEvent | KeyboardEvent, folder: any) => {
        if (selectedFolder.id !== folder.folderID) {
            e.stopPropagation();
            e.preventDefault();
            deselectAll();
            dispatch(contentLibraryAction.setActiveFolder(folder.folderID));
        }
    };

    const onConfirmFolderRenameClick = async (e: MouseEvent | KeyboardEvent, folder: any) => {
        e.stopPropagation();
        e.preventDefault();
        setIsAddingNewFolder(false);
        if (newFolderName.length > MAX_FOLDER_NAME_CHAR) {
            eventService.notify(ERR_FOLDER_NAME_TOO_LONG, true);
            return;
        }
        await updateFolderName(newFolderName, folder.folderID);
        setEditingFolder(null);
        setNewFolderName('');
    };

    const onCancelFolderRenameClick = (e: MouseEvent | KeyboardEvent) => {
        e.stopPropagation();
        setEditingFolder(null);
        setNewFolderName('');
    };

    const onRenameFolderClick = (e: MouseEvent | KeyboardEvent, folder: any) => {
        e.stopPropagation();
        setIsAddingNewFolder(false);
        setEditingFolder(folder.folderID);
        setNewFolderName(folder.folderName);
    };

    const onDeleteFolderClick = (e: MouseEvent | KeyboardEvent, folder: any) => {
        e.stopPropagation();
        toggleDeleteModal(folder.folderID);
    };

    const onConfirmFolderNameClick = (e: MouseEvent | KeyboardEvent) => {
        e.stopPropagation();
        if (newFolderName.length > MAX_FOLDER_NAME_CHAR) {
            eventService.notify(ERR_FOLDER_NAME_TOO_LONG, true);
            return;
        }
        addNewFolder();
        setNewFolderName('');
    };

    const onCancelFolderNameClick = (e: MouseEvent | KeyboardEvent) => {
        e.stopPropagation();
        setIsAddingNewFolder(false);
    };

    const onCreateNewFolderClick = (e: MouseEvent | KeyboardEvent) => {
        e.stopPropagation();
        setEditingFolder(null);
        setNewFolderName('');
        setIsAddingNewFolder(true);

        // if user clicks on the add folder button more than once, this will trigger, for the first time addNewFolderDiv will be null
        if (addNewFolderDiv && addNewFolderDiv.current) {
            addNewFolderDiv.current.scrollIntoView();
        }
    };

    return (
        <Styled.FolderColumn>
            {showDeleteWarningModal && (
                <DeleteModal
                    modalText="If you delete a folder from the library, it will delete all the contents within the folder but will NOT break the drafts /templates that use the files within the folder."
                    toggleDeleteModal={toggleDeleteModal}
                    onDelete={async (e) => {
                        e.stopPropagation();
                        deselectAll();
                        dispatch(contentLibraryAction.deleteFolder(deleteFolderID));
                        toggleDeleteModal(null);
                    }}
                />
            )}
            <Styled.FolderList canManageLibrary={canManageLibrary}>
                {folders.map((folder) => (
                    <Styled.FolderDiv
                        id="shared-library-folder"
                        key={folder.folderID}
                        onMouseOver={() => setFolderHovered(folder.folderID)}
                        onMouseLeave={() => setFolderHovered(null)}
                        onFocus={() => setFolderHovered(folder.folderID)}
                        selected={selectedFolder.id === folder.folderID}
                        onClick={(e: MouseEvent<HTMLInputElement>) => onSelectFolderClick(e, folder)}
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                            handleKeyPress(e, () => onSelectFolderClick(e, folder))
                        }
                        role="button"
                        aria-label="Select folder"
                        tabIndex={0}
                    >
                        <Styled.FolderIcon icon="folder" />
                        <Styled.FolderName selected={selectedFolder.id === folder.folderID}>
                            {editingFolder === folder.folderID ? (
                                <>
                                    <Styled.FolderNameInput
                                        value={newFolderName}
                                        onClick={(e: MouseEvent<HTMLInputElement>) => {
                                            e.stopPropagation();
                                        }}
                                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                                            handleKeyPress(e, () => e.stopPropagation())
                                        }
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            setNewFolderName(e.target.value);
                                        }}
                                        ref={editFolderNameInput}
                                        tabIndex={0}
                                    />
                                    <Styled.IconDiv>
                                        <Styled.IconsDiv
                                            disable={newFolderName.length < 3 || /^\s+$/.test(newFolderName)}
                                        >
                                            <Styled.Icon
                                                onClick={async (e: MouseEvent<SVGElement>) =>
                                                    onConfirmFolderRenameClick(e, folder)
                                                }
                                                onKeyDown={async (e: KeyboardEvent<SVGElement>) =>
                                                    handleKeyPress(
                                                        e as unknown as KeyboardEvent<HTMLElement>,
                                                        async () => onConfirmFolderRenameClick(e, folder)
                                                    )
                                                }
                                                tabIndex={0}
                                                color="true"
                                                icon="check"
                                                role="button"
                                                aria-hidden="false"
                                                aria-label="Confirm folder rename"
                                            />
                                        </Styled.IconsDiv>
                                        <Styled.Icon
                                            icon="times"
                                            color="true"
                                            onClick={(e: MouseEvent<SVGElement>) => onCancelFolderRenameClick(e)}
                                            onKeyDown={(e: KeyboardEvent<SVGElement>) =>
                                                handleKeyPress(e as unknown as KeyboardEvent<HTMLElement>, () =>
                                                    onCancelFolderRenameClick(e)
                                                )
                                            }
                                            tabIndex={0}
                                            role="button"
                                            aria-hidden="false"
                                            aria-label="Cancel folder rename"
                                        />
                                    </Styled.IconDiv>
                                </>
                            ) : (
                                <Styled.FolderNameDiv>
                                    <div data-tip data-for="folderID">
                                        {folderHovered === folder.folderID &&
                                            folder.folderName.length > MAX_FOLDER_CHAR_DISPLAY && (
                                                <ReactTooltip id="folderID" place="bottom" effect="solid">
                                                    <span style={{ fontWeight: 'bold' }}>{folder.folderName}</span>
                                                </ReactTooltip>
                                            )}
                                        <p>{returnFolderName(folder.folderName, folder.folderID)}</p>
                                    </div>
                                </Styled.FolderNameDiv>
                            )}

                            {folderHovered === folder.folderID && !editingFolder && canManageLibrary && (
                                <Styled.FolderIconsDiv>
                                    <Styled.IconsDiv disable={folders.length === 1} color="true">
                                        <Styled.Icon
                                            id="edit-folder"
                                            icon="pencil"
                                            role="button"
                                            aria-hidden="false"
                                            aria-label="Rename folder"
                                            onClick={(e: MouseEvent<SVGElement>) => onRenameFolderClick(e, folder)}
                                            onKeyDown={(e: KeyboardEvent<SVGElement>) =>
                                                handleKeyPress(e as unknown as KeyboardEvent<HTMLElement>, () =>
                                                    onRenameFolderClick(e, folder)
                                                )
                                            }
                                            onFocus={() => setFolderHovered(folder.folderID)}
                                            tabIndex={0}
                                        />
                                        <Styled.Icon
                                            id="delete-folder"
                                            icon="trash"
                                            role="button"
                                            aria-hidden="false"
                                            aria-label="Delete folder"
                                            onClick={(e: MouseEvent<SVGElement>) => onDeleteFolderClick(e, folder)}
                                            onKeyDown={(e: KeyboardEvent<SVGElement>) =>
                                                handleKeyPress(e as unknown as KeyboardEvent<HTMLElement>, () =>
                                                    onDeleteFolderClick(e, folder)
                                                )
                                            }
                                            onFocus={() => setFolderHovered(folder.folderID)}
                                            tabIndex={0}
                                        />
                                    </Styled.IconsDiv>
                                </Styled.FolderIconsDiv>
                            )}
                        </Styled.FolderName>
                    </Styled.FolderDiv>
                ))}
                {isAddingNewFolder && (
                    <Styled.FolderDiv ref={addNewFolderDiv} selected={false}>
                        <Styled.FolderIcon color="true" icon="folder" />
                        <Styled.FolderName>
                            <Styled.FolderNameInput
                                data-test-id="folder-name-input"
                                value={newFolderName}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setNewFolderName(e.target.value)}
                                ref={newFolderNameInput}
                            />
                            <Styled.IconDiv>
                                <Styled.IconsDiv
                                    disable={newFolderName.length < 3 || newFolderName.trim().length === 0}
                                >
                                    <Styled.Icon
                                        icon="check"
                                        aria-label="enter"
                                        onClick={(e: MouseEvent<SVGElement>) => onConfirmFolderNameClick(e)}
                                        onKeyDown={(e: KeyboardEvent<SVGElement>) =>
                                            handleKeyPress(e as unknown as KeyboardEvent<HTMLElement>, () =>
                                                onConfirmFolderNameClick(e)
                                            )
                                        }
                                        tabIndex={0}
                                    />
                                </Styled.IconsDiv>
                                <Styled.Icon
                                    icon="times"
                                    aria-label="close edit mode"
                                    onClick={(e: MouseEvent<SVGElement>) => onCancelFolderNameClick(e)}
                                    onKeyDown={(e: KeyboardEvent<SVGElement>) =>
                                        handleKeyPress(e as unknown as KeyboardEvent<HTMLElement>, () =>
                                            onCancelFolderNameClick(e)
                                        )
                                    }
                                    tabIndex={0}
                                />
                            </Styled.IconDiv>
                        </Styled.FolderName>
                    </Styled.FolderDiv>
                )}
            </Styled.FolderList>
            {canManageLibrary && (
                <Styled.NewFolderDiv
                    id="create-new-folder-shared-library"
                    onClick={(e: MouseEvent<HTMLDivElement>) => onCreateNewFolderClick(e)}
                    onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => handleKeyPress(e, () => onCreateNewFolderClick(e))}
                    tabIndex={0}
                    role="button"
                    aria-label="Create new folder"
                >
                    <Styled.FolderIcon icon="plus-circle" />
                    <Styled.CreateFolderText>Create a new folder</Styled.CreateFolderText>
                </Styled.NewFolderDiv>
            )}
        </Styled.FolderColumn>
    );
};

const mapStateToProps = (state: TAppState) => ({
    selectedFolder: _get(state, 'contentLibrary.selectedFolder', ''),
});

export default connect(mapStateToProps)(FolderColumn);
